import React, { useEffect, useRef, useState } from "react";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import { useDataApiClient } from "../../hooks/useApiClient";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Skeleton,
  SkeletonText,
  Tag,
} from "@chakra-ui/react";
import { HStack, ListItem, OrderedList, VStack } from "@chakra-ui/layout";
import { CherryHubDataApi } from "../../api/dataApiModels";
import { DocumentTime } from "../../styles/icons";
import { useSearchInput } from "../../hooks/useSearchInput";
import { useToast } from "../../hooks/useToast";

export const Apsc = () => {
  const isMounted = useRef(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { showErrorToast } = useToast();
  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Certificate Subject",
  });

  const [certificates, setCertificates] =
    useState<CherryHubDataApi.APSCExpirationStatus>();
  const [certificatesStatus, setCertificatesStatus] =
    useState<CherryHubDataApi.APSCExpirationStatus>();
  const [entries, setEntries] = useState<string[]>();
  const apiClient = useDataApiClient();

  const setCertificateStatus = async () => {
    const result = await apiClient.getASPCExpirationStatus();
    if (result.data && isMounted.current) {
      setCertificatesStatus(result.data);
      setCertificates(result.data);
    }

    if (!result.ok) {
      showErrorToast(result.message);
    }
  };

  const reloadConfig = async () => {
    const result = await apiClient.initApplePassKit();
    if (result.ok) {
      openSuccessModal(result.data);
    }
  };

  const openSuccessModal = (entries: string[]) => {
    setEntries(entries);
    onOpen();
  };

  useEffect(() => {
    setCertificateStatus();
  }, [isMounted]);

  useEffect(() => {
    var result: CherryHubDataApi.APSCExpirationStatus = {};
    Object.entries(certificatesStatus || {}).forEach(([ckey, cert]) => {
      if (
        cert.CertificateSubject?.toLowerCase()?.indexOf(
          debouncedQuery.toLowerCase()
        ) != -1
      )
        result[ckey] = cert;
    });
    setCertificates(debouncedQuery == "" ? certificatesStatus : result);
  }, [debouncedQuery]);

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Apple Pass Signing Certificates</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack w="100%">
          <HStack width="100%">
            {searchInputElement}
            <Button
              leftIcon={<DocumentTime />}
              colorScheme="cherryButton"
              onClick={reloadConfig}
            >
              Update Certificate
            </Button>
          </HStack>
          <ApscDetail certificates={certificates} query={debouncedQuery} />
        </VStack>
      </PageContent>

      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Certificate Success</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box height="60vh" overflowY="auto">
              <OrderedList spacing={3} pl={2} pr={2}>
                {entries?.map((entry, i) => {
                  return <ListItem key={i}>{entry}</ListItem>;
                })}
              </OrderedList>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="cherryButton" mr={3} onClick={onClose}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

interface ApscDetailProps {
  certificates: CherryHubDataApi.APSCExpirationStatus | undefined;
  query: string;
}

export const ApscDetail = (props: ApscDetailProps) => {
  return (
    <>
      {(props.certificates &&
        ((!Object.entries(props.certificates).length && (
          <Text>
            {props.query === ""
              ? "No certificates found."
              : `No certificates found matching query "${props.query}".`}
          </Text>
        )) ||
          Object.entries(props.certificates).map(([ckey, cert]) => {
            return (
              <Accordion key={ckey} allowToggle w="100%">
                <AccordionItem>
                  <h2>
                    <AccordionButton
                      _expanded={{ bg: "cherryButtonDark", color: "grey" }}
                    >
                      <Box flex="1" textAlign="left">
                        {ckey}
                      </Box>
                      <Box flexShrink={1}>
                        <Tag
                          size="sm"
                          bgColor={
                            cert.Status == "OK"
                              ? ""
                              : cert.Status == "Warning"
                              ? "orange"
                              : "cherryButton.500"
                          }
                          color={cert.Status == "OK" ? "" : "white"}
                        >
                          {cert.Status}
                        </Tag>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel width="100%">
                    <Text>
                      <b>Certificate Subject</b> : {cert?.CertificateSubject}
                    </Text>
                    <Text>
                      <b>Status</b> : {cert?.Status}
                    </Text>
                    <Text>
                      <b>Expiry Date</b> : {cert?.ExpiryDate}
                    </Text>
                    <Text>
                      <b>Details</b> : {cert?.Details}
                    </Text>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            );
          }))) || (
        <Box width="100%" padding="6" boxShadow="lg">
          <Skeleton height="40px" />
          <SkeletonText mt="4" noOfLines={4} spacing="4" />
          <Skeleton height="40px" />
          <SkeletonText mt="4" noOfLines={4} spacing="4" />
          <Skeleton height="40px" />
          <SkeletonText mt="4" noOfLines={4} spacing="4" />
          <Skeleton height="40px" />
          <SkeletonText mt="4" noOfLines={4} spacing="4" />
        </Box>
      )}
    </>
  );
};
